"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, CircleQuestionMark as HelpCircle, Clock, ShieldCheck, ArrowRight, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  tag: string;
}
const faqItems: FaqItem[] = [{
  id: "item-1",
  question: "How fast can an emergency plumber reach my home in Jonesboro?",
  answer: "Our local technicians are positioned directly in Jonesboro, GA. We dispatch emergency teams 24/7, with most urgent calls reached within 45 to 60 minutes depending on your exact neighborhood and current road conditions.",
  tag: "Response Time"
}, {
  id: "item-2",
  question: "What should I do immediately while waiting for the plumber?",
  answer: "Locate and shut off your main water valve immediately to minimize structural flooding. If the issue is localized to a sink or toilet, turn the fixture isolation valve clockwise. Keep clear of electrical outlets near standing water.",
  tag: "First Steps"
}, {
  id: "item-3",
  question: "Do you charge extra for late-night, weekend, or holiday emergencies?",
  answer: "We provide upfront, transparent pricing before any repair begins. We explain diagnostic fees and repair costs clearly over the phone and on site so there are never hidden surprises or inflated midnight fees.",
  tag: "Pricing & Rates"
}, {
  id: "item-4",
  question: "What plumbing issues qualify as a true emergency?",
  answer: "Burst or frozen pipes, active sewer backups, total loss of water service, severe indoor water heater leaks, and persistent overflowing toilets that cannot be stopped all require immediate emergency attention.",
  tag: "Emergency Types"
}, {
  id: "item-5",
  question: "Are your plumbers licensed, insured, and background checked?",
  answer: "Yes. Every technician sent to your Jonesboro property is fully licensed in the state of Georgia, comprehensively insured, and rigorously background checked for your peace of mind.",
  tag: "Trust & Safety"
}, {
  id: "item-6",
  question: "Which areas in Clayton County and surrounding regions do you cover?",
  answer: "We cover the entire city of Jonesboro and surrounding Clayton County communities, including Riverdale, Morrow, Forest Park, Lake City, and Lovejoy.",
  tag: "Service Area"
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const [activeItem, setActiveItem] = useState<string>("item-1");
  return <section id="frequently-asked-questions" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-5 md:px-8 max-w-[1240px]">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.35,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-bold tracking-wider uppercase mb-4">
            <HelpCircle className="size-4 text-primary" />
            <span>24/7 Emergency Plumbing FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 font-default">
            Frequently Asked Questions
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Fast, straightforward answers about our emergency plumbing dispatch, response windows, and upfront pricing in Jonesboro, GA.
          </p>
        </motion.div>

        {/* 2-Column Grid: Left Column FAQ Accordion, Right Column Emergency Action Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Main Accordion Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} className="lg:col-span-8 space-y-4">
            <Accordion type="single" collapsible value={activeItem} onValueChange={val => setActiveItem(val)} className="space-y-3">
              {faqItems.map((item, i) => <div key={item.id} data-index={i}>
                  <AccordionItem value={item.id} className="bg-card border border-border rounded-xl px-5 py-1 transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-sm">
                    <AccordionTrigger className="text-base md:text-lg font-semibold text-card-foreground text-left py-4 hover:no-underline focus-visible:ring-2 focus-visible:ring-ring">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 pr-2 text-left">
                        <span className="text-foreground font-default leading-snug">
                          {item.question}
                        </span>
                        <span className="self-start sm:self-auto text-xs px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium shrink-0">
                          {item.tag}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pt-1 pb-5 border-t border-border/50">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>

          {/* Right Column: Quick Triage & Contact Box */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.45,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-4 space-y-6">
            {/* Urgent Phone Dispatch Card */}
            <div className="rounded-2xl bg-primary text-primary-foreground p-6 md:p-7 shadow-lg relative overflow-hidden">
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-accent/15 rounded-full blur-2xl pointer-events-none" />
              
              <div className="flex items-center gap-2.5 text-accent font-bold text-xs uppercase tracking-wider mb-3">
                <span className="flex h-2.5 w-2.5 rounded-full bg-accent animate-pulse" />
                <span>Live Dispatch Available</span>
              </div>

              <h3 className="text-xl md:text-2xl font-bold mb-2 font-default text-primary-foreground">
                Plumbing Emergency Right Now?
              </h3>

              <p className="text-primary-foreground/80 text-sm leading-relaxed mb-6">
                Skip the contact form. Call our on-duty Jonesboro dispatcher directly for immediate routing and diagnostic guidance.
              </p>

              <div className="space-y-3">
                <Button asChild size="lg" className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 rounded-full font-bold shadow-md hover:shadow-lg transition-transform hover:-translate-y-0.5 text-base">
                  <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-5" />
                    <span>Call (470) 847-9076</span>
                  </a>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full h-11 bg-primary/20 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground rounded-full font-semibold text-sm">
                  <Link to="/contact/" className="flex items-center justify-center gap-2">
                    <span>Submit Service Request</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Quick Guarantees Box */}
            <div className="rounded-xl bg-card border border-border p-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-secondary text-primary shrink-0">
                  <Clock className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-card-foreground">24/7 Jonesboro Presence</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">Continuous day and night emergency crews across Clayton County.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 pt-3 border-t border-border">
                <div className="p-2 rounded-lg bg-secondary text-primary shrink-0">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-card-foreground">Upfront, Honest Pricing</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">Clear breakdown and approval before work begins.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 pt-3 border-t border-border">
                <div className="p-2 rounded-lg bg-secondary text-primary shrink-0">
                  <AlertCircle className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-card-foreground">Rapid Water Shutoff Guide</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">Phone guidance provided immediately to stop active flooding.</p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}